import { useEffect, useState } from 'react'
import { useCombobox } from 'downshift'
import { useFormikContext } from 'formik'
import { useDebounceValue } from '@sketch/utils'
import {
  InvitableUserFragment,
  useGetInvitableUsersQuery,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'

interface AutocompleteProps {
  workspaceIdentifier: string
  shareIdentifier: string
}

interface Values {
  shouldValidateEmail: boolean
  email: string
  userIdentifier: string | null
}

/**
 * Hook to orchestrate all the logic to create a users autocomplete
 */
const useAutocomplete = ({
  shareIdentifier,
  workspaceIdentifier,
}: AutocompleteProps) => {
  const { showToast } = useToast()
  const [items, setItems] = useState<InvitableUserFragment[]>([])

  const formik = useFormikContext<Values>()

  const {
    setFieldValue,
    validateForm,
    values: { email },
  } = formik

  const combobox = useCombobox({
    items,
    itemToString: item => item?.user?.name || '',
  })

  const { inputValue, selectedItem, reset } = combobox
  const debouncedValue = useDebounceValue(inputValue, 300)
  const isValueAMention = debouncedValue.charAt(0) === '@'
  const isValueSelected = selectedItem?.user?.name === inputValue
  const isValueSelectedDebounced = selectedItem?.user?.name === debouncedValue
  const isValueTooShort = debouncedValue.length === 0
  const isValueEmpty = inputValue.trim() === ''
  const skip = isValueTooShort || isValueSelectedDebounced || isValueEmpty

  const search = isValueAMention ? debouncedValue.substring(1) : debouncedValue

  useGetInvitableUsersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      shareIdentifier,
      workspaceIdentifier,
      search,
    },
    skip,
    onCompleted(data) {
      setItems(data?.invitableUsers.entries ?? [])
    },
    onError() {
      showToast('Failed to autocomplete name.', 'negative')
    },
  })

  // If we're skipping the query, then clear the items
  useEffect(() => {
    if (skip) {
      setItems([])
    }
  }, [skip])

  // Reset combobox if Formik form is reset
  useEffect(() => {
    if (email === '') {
      reset()
    }
  }, [email, reset])

  // Validate values on change to enable/disable
  // submit button accordingly
  // Need to use this due to bug in Formik:
  // https://github.com/formium/formik/issues/2083#issuecomment-734628995
  useEffect(() => {
    validateForm(formik.values)
  }, [validateForm, formik.values])

  useEffect(() => {
    // When the field value does not match the combobox selection
    // then we indicate to formik we should be treating it as a plain email value
    setFieldValue('shouldValidateEmail', !isValueSelectedDebounced)
    // Set the userIdentifier if we are using value from the autocomplete. Otherwise, we will
    // send the plain email
    setFieldValue(
      'userIdentifier',
      isValueSelectedDebounced && selectedItem?.identifier
    )
    // Set the formik value used to make the mutation to either combobox selection
    // or the input plain value, depending on whether a combobox item has been selected
    setFieldValue(
      'email',
      isValueSelectedDebounced ? selectedItem?.user?.email : inputValue
    )
  }, [isValueSelectedDebounced, inputValue, selectedItem, setFieldValue])

  return {
    items,
    formik,
    combobox,
    valueSelected: selectedItem?.user?.name || '',
    isValueSelected,
  }
}

export { useAutocomplete }
